import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { api } from "../lib/api";
import { PageHeader } from "../components/PageHeader";
import { Input } from "../components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Newspaper, Search, Archive, X } from "lucide-react";

const categories = ["Semua", "Berita", "Pengumuman", "Kegiatan", "Siaran Pers"];

const monthLabel = (ym) => {
  const [y, m] = ym.split("-");
  return new Date(Number(y), Number(m) - 1, 1).toLocaleDateString("id-ID", { month: "long", year: "numeric" });
};

export default function Berita() {
  const [news, setNews] = useState([]);
  const [searchParams, setSearchParams] = useSearchParams();
  const cat = searchParams.get("kategori") || "Semua";
  const arsip = searchParams.get("arsip") || "semua";
  const q = searchParams.get("q") || "";

  useEffect(() => {
    api.get("/news").then((r) => setNews(r.data)).catch(() => {});
  }, []);

  const setParam = (key, value, emptyValue) => {
    const next = new URLSearchParams(searchParams);
    if (!value || value === emptyValue) next.delete(key);
    else next.set(key, value);
    setSearchParams(next, { replace: true });
  };

  const archives = useMemo(() => {
    const map = {};
    news.forEach((n) => {
      const ym = (n.created_at || "").slice(0, 7);
      if (ym) map[ym] = (map[ym] || 0) + 1;
    });
    return Object.entries(map).sort((a, b) => b[0].localeCompare(a[0]));
  }, [news]);

  const filtered = useMemo(() => {
    const term = q.trim().toLowerCase();
    return news.filter((n) => {
      if (cat !== "Semua" && n.category !== cat) return false;
      if (arsip !== "semua" && (n.created_at || "").slice(0, 7) !== arsip) return false;
      if (term && !`${n.title} ${n.content}`.toLowerCase().includes(term)) return false;
      return true;
    });
  }, [news, cat, arsip, q]);

  const hasFilter = cat !== "Semua" || arsip !== "semua" || q;

  return (
    <div data-testid="berita-page">
      <PageHeader title="Berita & Kegiatan" subtitle="Informasi terbaru dan dokumentasi kegiatan penanggulangan bencana di Kabupaten Kayong Utara" />
      <section className="max-w-7xl mx-auto px-4 py-16">
        <div className="flex flex-col lg:flex-row lg:items-center gap-4 mb-10">
          <div className="flex flex-wrap gap-2 flex-1" data-testid="berita-filter">
            {categories.map((c) => (
              <button
                key={c}
                onClick={() => setParam("kategori", c, "Semua")}
                data-testid={`berita-filter-${c.toLowerCase().replace(/\s/g, "-")}`}
                className={`btn-pop px-5 py-2 rounded-full text-sm font-bold ${
                  cat === c ? "bg-orange-600 text-white" : "bg-white border border-slate-200 text-slate-600 hover:border-orange-400 hover:text-orange-600"
                }`}
              >
                {c}
              </button>
            ))}
          </div>
          <div className="flex gap-3">
            <div className="relative w-full sm:w-64">
              <Search size={15} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400" />
              <Input
                value={q}
                onChange={(e) => setParam("q", e.target.value, "")}
                placeholder="Cari berita..."
                className="pl-9 rounded-full"
                data-testid="berita-search-input"
              />
            </div>
            <Select value={arsip} onValueChange={(v) => setParam("arsip", v, "semua")}>
              <SelectTrigger className="w-48 rounded-full" data-testid="berita-arsip-select">
                <span className="flex items-center gap-2 truncate"><Archive size={14} className="text-slate-400 shrink-0" /><SelectValue /></span>
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="semua">Semua Arsip</SelectItem>
                {archives.map(([ym, count]) => (
                  <SelectItem key={ym} value={ym} data-testid={`arsip-${ym}`}>{monthLabel(ym)} ({count})</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        {hasFilter && (
          <div className="flex items-center gap-3 mb-8 text-sm text-slate-500" data-testid="berita-result-info">
            <span>
              Menampilkan <span className="font-bold text-slate-900">{filtered.length}</span> hasil
              {q && <> untuk "<span className="font-semibold text-orange-600">{q}</span>"</>}
              {arsip !== "semua" && <> · arsip <span className="font-semibold">{monthLabel(arsip)}</span></>}
              {cat !== "Semua" && <> · kategori <span className="font-semibold">{cat}</span></>}
            </span>
            <button onClick={() => setSearchParams({}, { replace: true })} className="inline-flex items-center gap-1 text-orange-600 font-bold hover:underline" data-testid="berita-reset-filter">
              <X size={13} /> Hapus filter
            </button>
          </div>
        )}

        {filtered.length === 0 ? (
          <div className="border-2 border-dashed border-slate-200 rounded-xl p-16 text-center text-slate-400" data-testid="berita-empty">
            <Newspaper className="mx-auto mb-3" size={32} />
            {hasFilter ? "Tidak ada berita yang cocok dengan pencarian/filter Anda." : "Belum ada berita yang dipublikasikan."}
          </div>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {filtered.map((n) => (
              <Link key={n.id} to={`/berita/${n.id}`} className="group hover-lift border border-slate-200 bg-white rounded-xl overflow-hidden hover:border-orange-400" data-testid={`berita-card-${n.id}`}>
                {n.image_url && <img src={n.image_url} alt={n.title} className="w-full h-44 object-cover" />}
                <div className="p-6">
                  <span className="section-label text-sky-600">{n.category}</span>
                  <h3 className="font-heading font-bold text-slate-900 mt-2 group-hover:text-orange-600 transition-colors">{n.title}</h3>
                  <p className="text-sm text-slate-500 mt-2 line-clamp-3">{n.content}</p>
                  <div className="flex items-center justify-between mt-4">
                    <p className="font-mono text-xs text-slate-400">{new Date(n.created_at).toLocaleDateString("id-ID", { day: "numeric", month: "long", year: "numeric" })}</p>
                    <span className="inline-flex items-center gap-1 text-xs font-bold text-orange-600 group-hover:gap-2 transition-all">Selengkapnya <span>→</span></span>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        )}
      </section>
    </div>
  );
}
